import z from 'zod';
import { AllCharLabelSchema, UUIDSchema, UnixTimestampSchema } from '../common/schemas';
import {
  DBJsonObjectSchema,
  DBNumericSchema,
  DBSha256Schema,
  DBTimestampSchema,
  JsonObjectSchema,
  PageFractionSchema,
  Sha256Schema,
} from '../common/sql';
import { QuestionStatus } from './enums';

const LATEX_MAX = 20000;
const LatexSchema = z.string().min(1).max(LATEX_MAX);
/**
 * A node's own text. It can be empty on a node whose parts hold all the text,
 * such as a question number printed above its parts. An empty leaf is a failed
 * transcription, which the digitiser rejects.
 */
const NodeLatexSchema = z.string().max(LATEX_MAX);
const PlaceholderTokenSchema = z
  .string()
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Must be a lowercase hyphenated slug');

/**
 * DB Schemas
 *
 * A root question and each of its parts are all rows in `questions`. `status`
 * is nullable because a CHECK allows it on roots only.
 */

export const DBQuestionStatusSchema = z.enum(QuestionStatus);

export const DBQuestionSchema = z.object({
  id: UUIDSchema,
  parentQuestionId: UUIDSchema.nullable(),
  position: z.number().int().min(0),
  partLabel: z.string().min(1).max(32).nullable(),
  bodyLatex: NodeLatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  status: DBQuestionStatusSchema.nullable(),
  subjectId: UUIDSchema.nullable(),
  independentlyUsable: z.boolean(),
  derivedFromQuestionId: UUIDSchema.nullable(),
  supersededByQuestionId: UUIDSchema.nullable(),
  selfHash: DBSha256Schema.nullable(),
  treeHash: DBSha256Schema.nullable(),
  createdBy: UUIDSchema.nullable(),
  publishedBy: UUIDSchema.nullable(),
  publishedAt: DBTimestampSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBStimulusSchema = z.object({
  id: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  createdBy: UUIDSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBQuestionCriterionSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  position: z.number().int().min(0),
  descriptorLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  marks: z.number().int().min(0),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBCriterionPointSchema = z.object({
  id: UUIDSchema,
  criterionId: UUIDSchema,
  position: z.number().int().min(0),
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBExemplarAnswerSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  provenanceId: UUIDSchema,
  sourceSnippetAssetId: UUIDSchema.nullable(),
  isPrimary: z.boolean(),
  position: z.number().int().min(0),
  createdBy: UUIDSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBAlternativeAnswerSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  provenanceId: UUIDSchema,
  notes: AllCharLabelSchema.min(1).max(2048).nullable(),
  position: z.number().int().min(0),
  createdBy: UUIDSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBMarkerNoteSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  provenanceId: UUIDSchema,
  position: z.number().int().min(0),
  createdBy: UUIDSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBQuestionOptionSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  position: z.number().int().min(0),
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  isCorrect: z.boolean(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

/**
 * The option columns shown to students. Listed one by one so a new option
 * column stays hidden from students until it is added here.
 */
const displayOptionColumns = {
  id: true,
  questionId: true,
  position: true,
  bodyLatex: true,
  latexValid: true,
  latexValidation: true,
  createdAt: true,
  updatedAt: true,
} as const satisfies Partial<
  Record<keyof typeof DBQuestionOptionSchema.shape & keyof typeof QuestionOptionSchema.shape, true>
>;

/** An option as a student sees it, without `isCorrect`. */
export const DBQuestionDisplayOptionSchema = DBQuestionOptionSchema.pick(displayOptionColumns);

export const DBQuestionDiagramSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema.nullable(),
  stimulusId: UUIDSchema.nullable(),
  assetId: UUIDSchema,
  placeholderToken: PlaceholderTokenSchema,
  caption: AllCharLabelSchema.min(1).max(512).nullable(),
  sourcePage: z.number().int().positive().nullable(),
  bboxX0: DBNumericSchema.nullable(),
  bboxY0: DBNumericSchema.nullable(),
  bboxX1: DBNumericSchema.nullable(),
  bboxY1: DBNumericSchema.nullable(),
  position: z.number().int().min(0),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBQuestionStimulusLinkSchema = z.object({
  questionId: UUIDSchema,
  stimulusId: UUIDSchema,
  position: z.number().int().min(0),
  createdAt: DBTimestampSchema,
});

export const DBQuestionDependencySchema = z.object({
  id: UUIDSchema,
  fromQuestionId: UUIDSchema,
  toQuestionId: UUIDSchema,
  dependencyTypeId: UUIDSchema,
  createdAt: DBTimestampSchema,
});

/**
 * Client Schemas
 */

export const QuestionStatusSchema = z.enum(QuestionStatus).openapi({
  description: 'Lifecycle status, set on roots only. Parts follow their root.',
  example: QuestionStatus.Published,
});
export const QuestionIdSchema = UUIDSchema;
export const QuestionBodyLatexSchema = NodeLatexSchema.openapi({
  description: 'This node’s own text, in LaTeX. Empty on a node whose parts hold all the text.',
  example: 'A projectile is launched at 25 m/s.',
});
export const QuestionSelfHashSchema = Sha256Schema;
export const QuestionPartLabelSchema = z.string().min(1).max(32).nullable().openapi({
  description:
    'The label the source document gave this part, relative to its parent: 7(b)(ii) is stored as (ii) under part (b). Kept for provenance only.',
  example: '(b)',
});

export const QuestionSchema = z.object({
  id: UUIDSchema,
  parentQuestionId: UUIDSchema.nullable(),
  position: z.number().int().min(0),
  partLabel: QuestionPartLabelSchema,
  bodyLatex: QuestionBodyLatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  status: QuestionStatusSchema.nullable(),
  subjectId: UUIDSchema.nullable(),
  independentlyUsable: z.boolean(),
  derivedFromQuestionId: UUIDSchema.nullable(),
  supersededByQuestionId: UUIDSchema.nullable(),
  selfHash: Sha256Schema.nullable(),
  treeHash: Sha256Schema.nullable(),
  createdBy: UUIDSchema.nullable(),
  publishedBy: UUIDSchema.nullable(),
  publishedAt: UnixTimestampSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const StimulusSchema = z.object({
  id: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  createdBy: UUIDSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const QuestionCriterionSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  position: z.number().int().min(0),
  descriptorLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  marks: z
    .number()
    .int()
    .min(0)
    .openapi({
      description:
        'Whole marks, added across criteria. Consecutive half-mark criteria in the source are ' +
        'joined into one criterion worth their total.',
      example: 2,
    }),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const CriterionPointSchema = z.object({
  id: UUIDSchema,
  criterionId: UUIDSchema,
  position: z.number().int().min(0),
  bodyLatex: LatexSchema.openapi({
    description: 'One of the things this criterion requires before its marks are awarded.',
    example: 'States that the reaction is exothermic',
  }),
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const ExemplarAnswerSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  provenanceId: UUIDSchema,
  sourceSnippetAssetId: UUIDSchema.nullable(),
  isPrimary: z.boolean(),
  position: z.number().int().min(0),
  createdBy: UUIDSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const AlternativeAnswerSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  provenanceId: UUIDSchema,
  notes: AllCharLabelSchema.min(1).max(2048).nullable(),
  position: z.number().int().min(0),
  createdBy: UUIDSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const MarkerNoteSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  provenanceId: UUIDSchema,
  position: z.number().int().min(0),
  createdBy: UUIDSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const QuestionOptionSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  position: z.number().int().min(0),
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  isCorrect: z.boolean(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

/** An option as a student sees it, without `isCorrect`. */
export const QuestionDisplayOptionSchema = QuestionOptionSchema.pick(displayOptionColumns);

export const QuestionDiagramSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema.nullable(),
  stimulusId: UUIDSchema.nullable(),
  assetId: UUIDSchema,
  placeholderToken: PlaceholderTokenSchema.openapi({
    description: 'The slug in the body’s [[diagram:…]] token',
    example: 'projectile-path',
  }),
  caption: AllCharLabelSchema.min(1).max(512).nullable(),
  sourcePage: z.number().int().positive().nullable(),
  bboxX0: PageFractionSchema.nullable(),
  bboxY0: PageFractionSchema.nullable(),
  bboxX1: PageFractionSchema.nullable(),
  bboxY1: PageFractionSchema.nullable(),
  position: z.number().int().min(0),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

/** Where a stimulus sits among a node's parts. Stimuli and parts share one position sequence. */
export const QuestionStimulusLinkSchema = z.object({
  questionId: UUIDSchema,
  stimulusId: UUIDSchema,
  position: z.number().int().min(0),
  createdAt: UnixTimestampSchema,
});

/** A dependency between two parts of the same question. */
export const QuestionDependencySchema = z.object({
  id: UUIDSchema,
  fromQuestionId: UUIDSchema,
  toQuestionId: UUIDSchema,
  dependencyTypeId: UUIDSchema,
  createdAt: UnixTimestampSchema,
});
